/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Chip,
} from '@mui/material'
import useSelectOptions from '../../../hooks/useSelectOptions'
import { closeMaintenanceFields } from '../../../constants/forms/manutencao/maintenanceFields'
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from '../../../utils/objectUtils'
import useForm from '../../../hooks/useForm'
import Form from '../../../components/forms/Form'
import SectionHeader from '../../../components/forms/SectionHeader'
import SimpleBody from '../../../components/forms/SimpleBody'
import useBaseService from '../../../hooks/services/useBaseService'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { entities } from '../../../constants/entities'
import useLoader from '../../../hooks/useLoader'
import { useOrgan } from '../../../hooks/useOrgan'
import { organizationPrimaryColors } from '../../../layouts/PermanentSidebarMUI'
import { EquipmentDestination } from '../../../enums/equipment_destination-enum'
import { TechnicianStatus } from '../../../enums/technician_status-enum'
import MovementEnum, { getMovementName } from '../../../enums/movement-enum'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import useNotification from '../../../hooks/useNotification'

const originalDestinationOptions = [
  {
    value: EquipmentDestination.KEEP_RENTED,
    label:
      'Manter alugado (Quando o técnico está no local consertando o equipamento)',
  },
  {
    value: EquipmentDestination.KEEP_RESERVED,
    label:
      'Manter reservado (Quando o técnico está no local consertando o equipamento)',
  },
  {
    value: EquipmentDestination.RETURN_TO_CLIENT,
    label:
      'Devolver equipamento para cliente (Quando o técnico está na base consertando o equipamento)',
  },
  {
    value: EquipmentDestination.WILL_BE_REPLACED,
    label: 'Será substituído',
  },
  { value: EquipmentDestination.REQUEST_DISPOSAL, label: 'Solicitar baixa' },
  {
    value: EquipmentDestination.AVAILABLE_FOR_RENT,
    label: 'Liberar para locação',
  },
  {
    value: EquipmentDestination.INTERNAL_USE,
    label: 'Definir equipamento como USO INTERNO',
  },
  {
    value: EquipmentDestination.WARRANTY,
    label: 'Definir equipamento como GARANTIA',
  },
]

const CloseMaintenance = ({ open, onClose, maintenance }) => {
  const navigate = useNavigate()

  const { showLoader, hideLoader } = useLoader()
  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)

  const { showNotification } = useNotification()

  const queryClient = useQueryClient()

  const [closeMaintenanceSection] = closeMaintenanceFields

  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([closeMaintenanceSection]),
    [closeMaintenanceSection]
  )

  const equipment = maintenance?.equipment_id ?? null
  const productItem = maintenance?.product_item_id ?? null

  const [destinationOptionsRaw, setDestinationOptionsRaw] = useState(
    originalDestinationOptions
  )

  // Buscar informaçõe sobre o equipamento da manutenção
  const { data: equipmentData } = useQueryWithLoader({
    queryKey: ['equipment', equipment],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.equipments.getById(equipment)
      )
      return response.result
    },
    enabled: !!equipment,
  })

  // Necessario identificar também se esse equipamento ta sendo substituido
  // ou seja verificar se teve alguma ordem de serviço de substituição criada para ele
  // sendo a última ordem de serviço criada para ele

  const lastReplacementServiceOrderItem =
    equipmentData?.last_replacement_service_order_item ?? null
  const lastMovementType =
    equipmentData?.last_relevant_movement_item?.movement_type_id ?? null

  useEffect(() => {
    setDestinationOptionsRaw(originalDestinationOptions)

    if (!maintenance?.technician_status_id) return

    if (equipment) {
      // *----------------------------------------------------------------------------------------------------------------------------------------
      // * Para os casos onde o equipamento esteve em um fluxo de aluguel e devolução
      // *----------------------------------------------------------------------------------------------------------------------------------------
      const isDevolucao = [
        MovementEnum.DEVOLUCAO,
        MovementEnum.DEVOLUCAO_MANUTENCAO,
      ].includes(lastMovementType);

      const isAluguel = lastMovementType === MovementEnum.ALUGUEL;
      const isReserva = lastMovementType === MovementEnum.RESERVA;

      if ((isAluguel || isReserva || isDevolucao) && !lastReplacementServiceOrderItem) {
        // Se o equipamento estava alugado e o status do técnico for LIBERADO
        // opções de destination:
        // - Manter Alugado (Quando o técnico está no local consertanto o equipamento).
        // - Devolver para o cliente (Quando o técnico está na base consertando o equipamento).
        if (
          isAluguel &&
          [TechnicianStatus.RELEASED.value].includes(
            maintenance?.technician_status_id
          )
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [
                EquipmentDestination.KEEP_RENTED,
                EquipmentDestination.RETURN_TO_CLIENT,
              ].includes(option.value)
            )
          )
        }

        // Se o equipamento estava alugado e o status do técnico for SEM REPARO
        // opções de destination:
        // - Será substituido
        if (
          isAluguel &&
          maintenance?.technician_status_id ===
            TechnicianStatus.WITHOUT_REPAIR.value
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.WILL_BE_REPLACED].includes(option.value)
            )
          )
        }

        // Se o equipamento estava reservado e o status do técnico for LIBERADO
        // opções de destination:
        // - Manter reservado (Quando o técnico está no local consertanto o equipamento).
        // - Devolver para o cliente (Quando o técnico está na base consertando o equipamento).
        if (
          isReserva &&
          [TechnicianStatus.RELEASED.value].includes(
            maintenance?.technician_status_id
          )
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [
                EquipmentDestination.KEEP_RESERVED,
                EquipmentDestination.RETURN_TO_CLIENT,
              ].includes(option.value)
            )
          )
        }

        // Se o equipamento estava reservado e o status do técnico for SEM REPARO
        // opções de destination:
        // - Será substituido
        if (
          isReserva &&
          maintenance?.technician_status_id ===
            TechnicianStatus.WITHOUT_REPAIR.value
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.WILL_BE_REPLACED].includes(option.value)
            )
          )
        }

        // Se o equipamento foi devolvido (normal ou a partir de manutenção) e o status do técnico for LIBERADO
        // opções de destination:
        // - Liberar para locação
        if (
          isDevolucao &&
          [TechnicianStatus.RELEASED.value].includes(
            maintenance?.technician_status_id
          )
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.AVAILABLE_FOR_RENT].includes(option.value)
            )
          )
        }

        // Se o equipamento foi devolvido (normal ou a partir de manutenção) e o status do técnico for SEM REPARO
        // opções de destination:
        // - Solicitar baixa
        if (
          isDevolucao &&
          maintenance?.technician_status_id ===
            TechnicianStatus.WITHOUT_REPAIR.value
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.REQUEST_DISPOSAL].includes(option.value)
            )
          )
        }

        // Se o equipamento foi devolvido (normal ou a partir de manutenção) e o status do técnico for USO INTERNO
        // opções de destination:
        // - Definir equipamento como USO INTERNO
        if (
          isDevolucao &&
          maintenance?.technician_status_id ===
            TechnicianStatus.INTERNAL_USE.value
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.INTERNAL_USE].includes(option.value)
            )
          )
        }
      }

      // *----------------------------------------------------------------------------------------------------------------------------------------
      // * Para os casos onde o equipamento esteve em um fluxo de substituição
      // *----------------------------------------------------------------------------------------------------------------------------------------
      const isSubstituicao = [
        MovementEnum.SUBSTITUICAO,
        MovementEnum.SUBSTITUICAO_MANUTENCAO,
      ].includes(lastMovementType);

      if (isSubstituicao && lastReplacementServiceOrderItem) {
        // Se o equipamento foi substituido e o status do técnico for LIBERADO
        // opções de destination:
        // - Liberar para locação
        if (
          [TechnicianStatus.RELEASED.value].includes(
            maintenance?.technician_status_id
          )
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.AVAILABLE_FOR_RENT].includes(option.value)
            )
          )
        }

        // Se o equipamento foi substituido e o status do técnico for SEM REPARO
        // opções de destination:
        // - Solitar baixa
        if (
          maintenance?.technician_status_id ===
          TechnicianStatus?.WITHOUT_REPAIR?.value
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.REQUEST_DISPOSAL].includes(option.value)
            )
          )
        }

        // Se o equipamento foi substituido e o status do técnico for USO INTERNO
        // opções de destination:
        // - Denifir equipamento como USO INTERNO
        if (
          maintenance?.technician_status_id ===
          TechnicianStatus.INTERNAL_USE.value
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.INTERNAL_USE].includes(option.value)
            )
          )
        }
      }

      // *----------------------------------------------------------------------------------------------------------------------------------------
      // * Para casos onde o equipamento não teve movimentação
      // *----------------------------------------------------------------------------------------------------------------------------------------
      else if (!lastMovementType) {
        // Se o equipamento que não possui movimentação e o status do técnico for LIBERADO
        // opções de destination:
        // - Liberar para locação
        if (
          [TechnicianStatus.RELEASED.value].includes(
            maintenance?.technician_status_id
          )
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.AVAILABLE_FOR_RENT].includes(option.value)
            )
          )
        }

        // Se o equipamento que não possui movimentação e o status do técnico for SEM REPARO
        // opções de destination:
        // - Solitar baixa
        if (
          maintenance?.technician_status_id ===
          TechnicianStatus?.WITHOUT_REPAIR?.value
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.REQUEST_DISPOSAL].includes(option.value)
            )
          )
        }

        // Se o equipamento que não possui movimentação e o status do técnico for USO INTERNO
        // opções de destination:
        // - Denifir equipamento como USO INTERNO
        if (
          maintenance?.technician_status_id ===
          TechnicianStatus?.INTERNAL_USE?.value
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.INTERNAL_USE].includes(option.value)
            )
          )
        }

        // Se o equipamento que não possui movimentação e o status do técnico for GARANTIA
        // opções de destination:
        // - Denifir equipamento como GARANTIA
        if (
          maintenance?.technician_status_id ===
          TechnicianStatus?.WARRANTY?.value
        ) {
          setDestinationOptionsRaw(
            originalDestinationOptions.filter((option) =>
              [EquipmentDestination.WARRANTY].includes(option.value)
            )
          )
        }
      }
    }
  }, [
    maintenance?.technician_status_id,
    originalDestinationOptions,
    equipment,
    lastMovementType,
    lastReplacementServiceOrderItem,
  ])

  const destinationMaintenanceSelect = useSelectOptions({
    fieldId: 'maintenance.destination',
    formData,
    rawOptions: destinationOptionsRaw,
    isMulti: false,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    setFormData,
  })

  useEffect(() => {
    const selectedDestination = formData.maintenance?.destination?.value
    if (
      selectedDestination &&
      !destinationOptionsRaw.some((option) => option.value === selectedDestination)
    ) {
      setFormData((prev) => ({
        ...prev,
        maintenance: {
          ...prev.maintenance,
          destination: null,
        },
      }))
    }
  }, [destinationOptionsRaw, formData.maintenance?.destination?.value, setFormData])

  const selects = {
    'maintenance.destination': destinationMaintenanceSelect,
  }

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const closeMaintenance = useMutation({
    mutationFn: async (data) => {
      showLoader()
      const response = await baseServicePost(
        entities.maintenances.close(maintenance?.id),
        data
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader()
        navigate('/manutencao/')
        queryClient.invalidateQueries(['maintenance'])
        queryClient.invalidateQueries(['equipment'])
        onClose()
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  async function handleSubmit() {
    try {
      if (!formData.maintenance?.destination?.value) {
        showNotification('warning', 'O campo de destino deve ser preenchido')
        return
      }

      if (
        equipment &&
        !destinationOptionsRaw.some(
          (option) => option.value === formData.maintenance.destination.value
        )
      ) {
        showNotification('warning', 'O destino selecionado não é válido')
        return
      }

      let payload = {
        maintenance: {
          destination: formData.maintenance.destination?.value,
          technician_status_id: maintenance.technician_status_id,
        },
      }

      // * Quando tiver produtos na manutenção
      // * Se tiver equipamento junto, manter o destination (necessário para transição de status)
      // * Se for só produto, remover o destination e mandar somente technician_status_id e product_quantity
      if (productItem && !equipment) {
        payload = {
          maintenance: {
            technician_status_id: payload.maintenance.technician_status_id,
            product_quantity: maintenance.product_quantity ?? 0,
          },
        }
      } else if (productItem && equipment) {
        payload.maintenance.product_quantity = maintenance.product_quantity ?? 0;
      }

      return await closeMaintenance.mutateAsync(payload)
    } catch (error) {
      console.error('Erro ao enviar requisição:', {
        status: error.status,
        message: error.message,
      })
    }
  }

  // Limpa o select ao fechar o modal
  useEffect(() => {
    if (!open) {
      // limpa o valor do select
      setFormData((prev) => ({
        ...prev,
        maintenance: {
          ...prev.maintenance,
          destination: null,
        },
      }))

      // reseta as opções para o padrão
      setDestinationOptionsRaw(originalDestinationOptions)
    }
  }, [open])

  const { selectedOrgan } = useOrgan()
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  // Se o equipamento estiver como um item de ordem de serviço de substituição, forçamos o tipo de movimento como SUBSTITUIÇÃO.
  // Caso contrário, usamos o último tipo de movimento registrado.
  const movementName = lastReplacementServiceOrderItem
    ? getMovementName(MovementEnum.SUBSTITUICAO)
    : getMovementName(lastMovementType)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus>
      <DialogTitle
        sx={{
          background: primaryColor,
          color: 'white',
          fontWeight: 'bold',
        }}>
        Fechar Manutenção
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <div className="container-fluid p-1">
          <Form
            initialFormData={formData}
            onSubmit={handleSubmit}
            textSubmit="Fechar Manutenção"
            textLoadingSubmit="Fechar Manutenção..."
            isLoading={closeMaintenance.isPending}
            showButtons={false}>
            {() => {
              return (
                <div key={closeMaintenanceSection.section}>
                  <SectionHeader section={closeMaintenanceSection.section} />

                  {/* Mostrar apenas quando for equipamento e ele tiver movimento */}
                  {equipment && lastMovementType && (
                    <Box
                      sx={{
                        ml: 1,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                      <Typography variant={'subtitle1'}>
                        Esse equipamento veio de um(a)
                      </Typography>
                      <Chip label={movementName} color="primary" />
                    </Box>
                  )}
                  <SimpleBody
                    fields={closeMaintenanceSection.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                  />
                </div>
              )
            }}
          </Form>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} color="primary">
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CloseMaintenance
